/**
 * StatementsListPage — top-level list of all uploaded supplier statements.
 *
 * Each row is one statement file (PDF or CSV). Drilling into a row opens
 * /statements/:id (the reconciliation page, scoped to that statement only)
 * so that lines from different uploads no longer stack into one mega-table.
 */
import React, { useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Button, IconButton, Tooltip, TextField,
  Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { setPendingStatementUpload } from '../utils/pendingStatementUpload';
import type { Statement, StatementSource } from '../data/types';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';

const FI_ORANGE = '#F57C00';

const SOURCE_LABEL: Record<StatementSource, string> = {
  pdf: 'PDF',
  'csv-smartbiz': 'SMARTBIZ CSV',
  'csv-selected-invoices': 'Selected-Invoices CSV',
  'csv-generic': 'CSV',
  manual: 'Manual',
};

const SOURCE_ICON = (source: StatementSource): React.ReactNode => {
  if (source === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 14, color: '#c62828' }} />;
  if (source.startsWith('csv')) return <TableViewIcon sx={{ fontSize: 14, color: '#2e7d32' }} />;
  return <DescriptionIcon sx={{ fontSize: 14, color: '#888' }} />;
};

const fmt = (n: number) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type FilterTab = 'all' | 'active' | 'in-progress' | 'completed' | 'archived';

export const StatementsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { statements, suppliers, bills, addStatement, setStatementStatus, deleteStatement } = useData();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [search,     setSearch]    = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSupplier, setUploadSupplier] = useState('');
  const [uploadProgress, setUploadProgress] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Statement | null>(null);
  // v1.14.2 — drag-state for the new drop-or-click upload zone
  const [isDragging, setIsDragging] = useState(false);
  // v1.14.6 — Which workflow to enter after upload. 'verify' = check each
  // statement line has a matching PO+Bill (no new bills). 'import' = create
  // new Bills from each line via the Bill Importer queue.
  const [uploadMode, setUploadMode] = useState<'verify' | 'import'>('verify');

  // ── Stats and filtered list ──────────────────────────────────────────────
  const stats = useMemo(() => ({
    all:           statements.length,
    active:        statements.filter(s => s.status === 'Active').length,
    inProgress:    statements.filter(s => s.status === 'In Progress').length,
    completed:     statements.filter(s => s.status === 'Completed').length,
    archived:      statements.filter(s => s.status === 'Archived').length,
  }), [statements]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return statements.filter(s => {
      if (filterTab === 'active'      && s.status !== 'Active')      return false;
      if (filterTab === 'in-progress' && s.status !== 'In Progress') return false;
      if (filterTab === 'completed'   && s.status !== 'Completed')   return false;
      if (filterTab === 'archived'    && s.status !== 'Archived')    return false;
      // 'all' includes everything
      if (q) {
        const haystack = `${s.filename} ${s.supplierName ?? ''} ${s.statementRef ?? ''} ${s.id}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [statements, filterTab, search]);

  // ── Upload handler (placeholder — real parse hook-up is on the reconciliation page) ──
  const detectSource = (filename: string): StatementSource => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return 'pdf';
    if (lower.includes('smartbiz')) return 'csv-smartbiz';
    if (lower.includes('selected-invoices')) return 'csv-selected-invoices';
    if (lower.endsWith('.csv')) return 'csv-generic';
    return 'manual';
  };

  const handleCreateStatement = () => {
    if (!uploadFile) return;
    setUploadProgress(true);
    // Create the statement shell — actual parsing happens on the reconciliation page
    const supplier = suppliers.find(s => s.id === uploadSupplier);
    const id = addStatement({
      filename: uploadFile.name,
      source: detectSource(uploadFile.name),
      supplierId: uploadSupplier || undefined,
      supplierName: supplier?.name,
      statementDate: new Date().toISOString().slice(0, 10),
      lines: [],
      status: 'Active',
      invoiceCount: 0,
      totalIncGst: 0,
      // v1.14.2 — user is about to drop invoices into this statement on the
      // next screen, so the Bill Importer queue owns the rollup totals.
      queueManaged: true,
    });
    // v1.14.3 — hand the actual File over to the Bill Importer so it can
    // trigger the right parse flow (PDF → queue + Claude vision parse,
    // CSV → CSV preview dialog). Without this the importer only ever
    // saw the statement metadata and the user landed on an empty page.
    setPendingStatementUpload(id, uploadFile);
    setUploadProgress(false);
    setUploadOpen(false);
    setUploadFile(null);
    setUploadSupplier('');
    // v1.14.6 — Route to the chosen workflow.
    if (uploadMode === 'verify') {
      navigate(`/statements/${id}`);
    } else {
      navigate(`/bills/import?statement=${id}`);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: BRAND }}>
            Statements
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#888' }}>
            Monthly supplier statements — track what you've received and what's reconciled.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<CloudUploadIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ backgroundColor: BRAND, fontSize: '0.8rem', '&:hover': { backgroundColor: '#5539a0' } }}>
          Upload Statement
        </Button>
      </Stack>

      {/* Info banner */}
      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{ mb: 2, fontSize: '0.78rem', '& .MuiAlert-message': { py: 0.4 } }}>
        Each <strong>statement</strong> is a single uploaded PDF or CSV from a supplier. Click <strong>Open</strong> to reconcile its
        lines against your FieldInsight POs on a dedicated screen — no more stacking lines from different statements.
      </Alert>

      {/*
        ── Action required: bills awaiting approval (v1.50.2) ─────────────────
        Surface bills in the approval queue at the top of Statements so AP can
        spot outstanding payables. One click drills into the Bills list filtered
        to Awaiting approval.
      */}
      {(() => {
        const awaitingApprovalBills = bills.filter(b => b.status === 'Awaiting approval');
        if (awaitingApprovalBills.length === 0) return null;
        const totalHeld = awaitingApprovalBills.reduce((s, b) => s + b.totalIncGst, 0);
        return (
          <Paper sx={{ p: 1.4, mb: 2, borderRadius: 2,
            background: 'linear-gradient(90deg, #fff8e1 0%, #fffde7 100%)',
            border: '1px solid #f9a825',
            display: 'flex', alignItems: 'center', gap: 1.3 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#f9a825',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HourglassEmptyIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#e65100', letterSpacing: -0.1 }}>
                {awaitingApprovalBills.length} bill{awaitingApprovalBills.length !== 1 ? 's' : ''} awaiting approval
                {' · '}
                <Box component="span" sx={{ color: '#5d4037' }}>{fmt(totalHeld)} pending sign-off</Box>
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#5d4037', mt: 0.2 }}>
                Reconciled supplier invoices waiting for approval before payment.
              </Typography>
            </Box>
            <Button size="small" variant="contained"
              onClick={() => navigate('/bills?status=Awaiting+approval')}
              sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
                backgroundColor: '#f57c00', py: 0.5, px: 1.2,
                '&:hover': { backgroundColor: '#e65100' } }}>
              Review bills
            </Button>
          </Paper>
        );
      })()}

      {/* Filters + search */}
      <Paper sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, fontSize: '0.72rem', textTransform: 'none', py: 0.5 } }}>
            <Tab label={`All · ${stats.all}`}                    value="all" />
            <Tab label={`Active · ${stats.active}`}              value="active" />
            <Tab label={`In Progress · ${stats.inProgress}`}     value="in-progress" />
            <Tab label={`Completed · ${stats.completed}`}        value="completed" />
            <Tab label={`Archived · ${stats.archived}`}          value="archived" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Search filename, supplier, ref…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: '#aaa', mr: 0.5 }} /> }}
            sx={{ width: 280, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 } }} />
        </Stack>
      </Paper>

      {/* List */}
      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }}>Statement / File</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }}>Supplier</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }} align="center">Source</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }} align="right"># Invoices</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }} align="right">Total inc GST</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#888', textTransform: 'uppercase' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: '#aaa' }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                    {statements.length === 0 ? 'No statements uploaded yet.' : 'No statements match this filter.'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>
                    {statements.length === 0
                      ? 'Click "Upload Statement" above to get started.'
                      : 'Try a different tab or clear the search box.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filtered.map(s => {
              return (
                <TableRow key={s.id} hover
                  onClick={() => navigate(`/statements/${s.id}`)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#fafafa' },
                    opacity: s.status === 'Archived' ? 0.6 : 1 }}>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {SOURCE_ICON(s.source)}
                      <Box>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#222' }} noWrap>
                          {s.filename}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#aaa', fontFamily: 'monospace' }}>
                          {s.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.75rem', color: '#444' }}>
                      {s.supplierName ?? <em style={{ color: '#999' }}>Mixed / unassigned</em>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>{s.statementDate}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={SOURCE_LABEL[s.source]} size="small" variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', borderColor: '#ddd' }} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{s.invoiceCount}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: FI_ORANGE }}>
                      {fmt(s.totalIncGst)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip status={s.status} />
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.3} justifyContent="flex-end">
                      {/*
                        v1.14.6 — Two distinct workflows per statement:
                          • Verify  → checks each line has a matching PO+Bill,
                                       no new bills created.
                          • Import  → creates one new Bill per statement line
                                       and routes through the Bill Importer.
                      */}
                      <Tooltip title="VERIFY — confirm each statement line has a matching PO & Bill in our records (no new bills created)" arrow>
                        <Button size="small" variant="contained" startIcon={<PlaylistAddCheckIcon sx={{ fontSize: '12px !important' }} />}
                          onClick={() => navigate(`/statements/${s.id}`)}
                          sx={{ fontSize: '0.65rem', py: 0.25, backgroundColor: BRAND,
                            '&:hover': { backgroundColor: '#5539a0' } }}>
                          Verify
                        </Button>
                      </Tooltip>
                      <Tooltip title="IMPORT BILLS — fan each statement line out into a brand-new Bill and reconcile to POs" arrow>
                        <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon sx={{ fontSize: '12px !important' }} />}
                          onClick={() => navigate(`/bills/import?statement=${s.id}`)}
                          sx={{ fontSize: '0.65rem', py: 0.25, color: '#6a1b9a', borderColor: '#6a1b9a',
                            '&:hover': { backgroundColor: '#f3e5f5', borderColor: '#4a148c' } }}>
                          Import Bills
                        </Button>
                      </Tooltip>
                      {s.status !== 'Archived' ? (
                        <Tooltip title="Archive — hides from active list but keeps history">
                          <IconButton size="small" onClick={() => setStatementStatus(s.id, 'Archived')}>
                            <ArchiveIcon sx={{ fontSize: 16, color: '#888' }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Restore from archive">
                          <IconButton size="small" onClick={() => setStatementStatus(s.id, 'Active')}>
                            <UnarchiveIcon sx={{ fontSize: 16, color: '#0277bd' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete permanently">
                        <IconButton size="small" onClick={() => setConfirmDelete(s)}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: '#bbb' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, color: BRAND }}>
          Upload Supplier Statement
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: '#666', mb: 2 }}>
            Drop a PDF statement or CSV export. The next screen will parse it and let you reconcile each line against your POs.
          </Typography>

          {/*
            ── File picker (v1.14.2 fix) ───────────────────────────────────
            Replaced the buggy HTML `hidden` attribute with a style-based hide
            (some browsers/MUI Dialog focus traps block programmatic clicks on
            inputs flagged with the HTML hidden attr). Also added native
            drag-and-drop on the dropzone so the file can be dragged in even
            when the click path is intercepted.
          */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.csv,application/pdf,text/csv"
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0] ?? null;
              setUploadFile(f);
              // Reset so re-picking the same file fires onChange next time.
              e.target.value = '';
            }} />

          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) setUploadFile(f);
            }}
            sx={{
              border: '2px dashed',
              borderColor: uploadFile ? BRAND : isDragging ? FI_ORANGE : '#bbb',
              borderRadius: 1.5, p: 3, textAlign: 'center', cursor: 'pointer',
              backgroundColor: uploadFile ? '#f5f0ff' : isDragging ? '#fff3e0' : '#fafafa',
              transition: 'all 0.15s',
              '&:hover': { borderColor: BRAND, backgroundColor: '#f5f0ff' },
              mb: 2,
            }}>
            <CloudUploadIcon sx={{ fontSize: 36,
              color: uploadFile ? BRAND : isDragging ? FI_ORANGE : '#aaa', mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700,
              color: uploadFile ? BRAND : isDragging ? FI_ORANGE : '#444' }}>
              {uploadFile
                ? uploadFile.name
                : isDragging
                  ? 'Release to attach file'
                  : 'Click to choose, or drag-and-drop a PDF or CSV'}
            </Typography>
            {uploadFile ? (
              <Stack direction="row" spacing={0.6} mt={0.6} justifyContent="center" alignItems="center">
                <Chip
                  label={`${(uploadFile.size / 1024).toFixed(1)} KB · ${detectSource(uploadFile.name)}`}
                  size="small" sx={{ fontSize: '0.65rem', backgroundColor: '#fff', border: `1px solid ${BRAND}40` }} />
                <Button size="small" onClick={e => { e.stopPropagation(); setUploadFile(null); }}
                  sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#888', minWidth: 0, p: 0.3 }}>
                  Clear
                </Button>
              </Stack>
            ) : (
              <Typography sx={{ fontSize: '0.7rem', color: '#888', mt: 0.4 }}>
                Accepted: .pdf, .csv (any size)
              </Typography>
            )}
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Supplier (optional — leave blank for mixed CSV)</InputLabel>
            <Select
              value={uploadSupplier}
              label="Supplier (optional — leave blank for mixed CSV)"
              onChange={e => setUploadSupplier(e.target.value)}>
              <MenuItem value=""><em>Mixed / unassigned</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*
            ── Workflow mode picker (v1.14.6) ─────────────────────────────────
            The user explicitly asked for two clearly-separated flows:
              • Verify   — confirm each line has a matching PO + Bill.
                            No new bills created. Output = variance report.
              • Import   — create one new Bill per statement line, route
                            through the Bill Importer queue. Output = N bills.
            We show two big cards so the choice is obvious. Default = Verify
            (matches the user's "back to earlier design on Statement
            reconciliation" intent).
          */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#888',
            textTransform: 'uppercase', mt: 2.5, mb: 1, letterSpacing: 0.4 }}>
            What's next?
          </Typography>
          <Stack direction="row" spacing={1.2}>
            <Paper
              onClick={() => setUploadMode('verify')}
              sx={{
                flex: 1, p: 1.4, cursor: 'pointer',
                border: uploadMode === 'verify' ? `2px solid ${BRAND}` : '2px solid #e8e8e8',
                backgroundColor: uploadMode === 'verify' ? '#f5f0ff' : '#fff',
                transition: 'all 0.12s',
                '&:hover': { borderColor: BRAND, backgroundColor: '#fafaff' },
              }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <PlaylistAddCheckIcon sx={{ color: uploadMode === 'verify' ? BRAND : '#888', mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800,
                    color: uploadMode === 'verify' ? BRAND : '#333' }}>
                    Verify Statement
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#666', mt: 0.3, lineHeight: 1.45 }}>
                    Check each line has a matching PO &amp; Bill in our records.
                    Flag missing or disputed amounts. <strong>No new bills.</strong>
                  </Typography>
                </Box>
              </Stack>
            </Paper>
            <Paper
              onClick={() => setUploadMode('import')}
              sx={{
                flex: 1, p: 1.4, cursor: 'pointer',
                border: uploadMode === 'import' ? `2px solid #6a1b9a` : '2px solid #e8e8e8',
                backgroundColor: uploadMode === 'import' ? '#f3e5f5' : '#fff',
                transition: 'all 0.12s',
                '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#faf5fb' },
              }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ReceiptLongIcon sx={{ color: uploadMode === 'import' ? '#6a1b9a' : '#888', mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800,
                    color: uploadMode === 'import' ? '#6a1b9a' : '#333' }}>
                    Import Bills
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#666', mt: 0.3, lineHeight: 1.45 }}>
                    Fan each statement line out into a <strong>new Bill</strong> and
                    reconcile in the Bill Importer queue.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>

          {uploadProgress && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadOpen(false); setUploadFile(null); setUploadSupplier(''); }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!uploadFile || uploadProgress}
            onClick={handleCreateStatement}
            sx={{ backgroundColor: uploadMode === 'verify' ? BRAND : '#6a1b9a',
              '&:hover': { backgroundColor: uploadMode === 'verify' ? '#5539a0' : '#4a148c' } }}>
            {uploadMode === 'verify' ? 'Create & Verify' : 'Create & Import Bills'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', color: '#b71c1c' }}>Delete this statement?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>
            This will permanently remove the statement <strong>{confirmDelete?.filename}</strong> and any unsaved
            reconciliation data on it. POs and bills already created from this statement are <strong>not</strong> affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (confirmDelete) deleteStatement(confirmDelete.id);
            setConfirmDelete(null);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatementsListPage;
